import {
  selectBasketStatus,
  selectInBasketQuantity,
} from '@epicuro-next/state/basket-state';
import { memoize } from '@epicuro-next/utilities';
import { Dictionary } from '@ngrx/entity';
import { createSelector } from '@ngrx/store';
import { BasketStatus } from 'graphql-api/baskets-api-interfaces';
import { DabData, Product, ProductKey } from 'graphql-api/gql-api-interfaces';

import { IDabEntry, IDabType } from '../interfaces';

import { adapter } from './dab-items.reducer';
import { selectDabRootState } from './reducer';

/**
 * Feature selector
 */
export const selectDabItemsState = createSelector(
  selectDabRootState,
  (state) => state.dabItems,
);

const { selectAll, selectEntities } = adapter.getSelectors(selectDabItemsState);

/**
 * Selector to get the ProductLists
 */
export const selectAllDABs = selectAll;

export const selectDabs = memoize(
  createSelector(
    selectEntities,
    (dabs: Dictionary<DabData>, data: ProductKey[]) =>
      filterMap<DabData>(dabs, data),
  ),
);

export const selectDABQuantities = memoize(
  (data: ProductKey & { rootId?: string }) =>
    createSelector(selectEntities, (dabs: Dictionary<DabData>) => {
      return filterMap<IDabEntry[]>(dabs, [data], parseDABentries)[
        data.productId
      ];
    }),
);

export const selectDABinBasket = (product: Product) =>
  createSelector(
    selectInBasketQuantity(product),
    selectBasketStatus,
    (inbasketQuantity: number, status: BasketStatus): IDabEntry | null => {
      return inbasketQuantity > 0 && status === BasketStatus.New
        ? {
            quantity: inbasketQuantity,
            type: IDabType.INBASKET,
            text: $localize`:productBasket|number of items in basket@@itemsInPBasket: item(s) in basket`,
          }
        : null;
    },
  );

function filterMap<T>(
  dabs: Dictionary<DabData>,
  data: ProductKey[],
  mapper?: (data: DabData) => T,
) {
  return data.reduce((aggr: Dictionary<IDabEntry[]>, dab) => {
    const dabData =
      Object.values(dabs).filter(({ productId, rootId, brandId }: DabData) => {
        return (
          brandId === dab.brandId &&
          (productId === dab.productId || rootId === dab.productId)
        );
      }) ?? [];
    return dabData
      ? {
          ...aggr,
          [dab.productId]: [
            ...(aggr[dab.productId] ?? []),
            ...(mapper ? dabData.map(mapper) : dabData),
          ].flat(),
        }
      : aggr;
  }, {}) as Dictionary<T>;
}

function parseDABentries(dab: DabData): IDabEntry[] {
  const dabList = [];
  if (dab.deliveredItemsQuantity) {
    const deliveredQuantity = dab.deliveredItemsQuantity.reduce(
      (total, entry) => total + entry,
      0,
    );
    if (deliveredQuantity !== 0) {
      dabList.push({
        quantity: deliveredQuantity,
        type: IDabType.DELIVERED,
        text: $localize`:productBasket|number of delivered items@@deliveredItemsInBasket: delivered items`,
      });
    }
  }
  if (dab.inboundItemsQuantity) {
    const inboundQuantity = dab.inboundItemsQuantity.reduce(
      (total, entry) => total + entry,
      0,
    );
    if (inboundQuantity !== 0) {
      dabList.push({
        quantity: inboundQuantity,
        type: IDabType.INBOUND,
        text: $localize`:productBasket|number of inbound items@@itemsInboundToYou: inbound items to you`,
      });
    }
  }

  return dabList;
}
